'use client';

import { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  Section,
  SectionHeader,
  SignupCTA,
  Reveal,
  RADII,
  SHADOW,
} from '../_shared';

/**
 * How It Works — animierte Setup-Checkliste als DOM-Nachbau (CRO 2026-07).
 *
 * War: Remotion-MP4 (deutsch hardcoded → falsche Sprache in 6 von 7
 * Locales, mobil unleserlich) + 4 Step-Cards, die dieselbe Story
 * doppelt erzählten. Neu (Codex): EIN Produkt-Panel, das die echte
 * Admin-Setup-Checkliste nachbaut — Schritte haken sich beim Scrollen
 * nacheinander ab, Progress-Ring füllt sich, „live"-Zeile erscheint.
 * i18n-nativ, gestochen scharf, 0 Video-Bytes.
 *
 * Promise = Reality: exakt diese Checkliste sieht der User im Admin.
 */

const STEP_KEYS = ['branding', 'services', 'hours', 'calendar'] as const;
const START_DELAY_MS = 600;
const STEP_INTERVAL_MS = 900;

export default function HowItWorks() {
  const t = useTranslations('landing.howItWorks');
  const panelRef = useRef<HTMLDivElement>(null);
  const [doneCount, setDoneCount] = useState(0);

  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional one-shot mount sync
      setDoneCount(STEP_KEYS.length);
      return;
    }

    const timers: number[] = [];
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            observer.disconnect();
            STEP_KEYS.forEach((_, i) => {
              timers.push(
                window.setTimeout(
                  () => setDoneCount(i + 1),
                  START_DELAY_MS + i * STEP_INTERVAL_MS,
                ),
              );
            });
          }
        }
      },
      // Spät feuern: Checkliste soll sich sichtbar abhaken, nicht schon
      // beim 35%-Anschnitt unterm Fold durch sein (User 2026-07-31)
      { threshold: 0.35, rootMargin: '0px 0px -25% 0px' },
    );
    observer.observe(el);
    return () => {
      observer.disconnect();
      timers.forEach((id) => clearTimeout(id));
    };
  }, []);

  const allDone = doneCount === STEP_KEYS.length;
  const pct = Math.round((doneCount / STEP_KEYS.length) * 100);

  return (
    <Section
      id="how-it-works"
      tone="default"
      ariaLabelledBy="how-it-works-heading"
    >
      <SectionHeader
        headline={t('headline')}
        description={t('sub')}
        headlineId="how-it-works-heading"
        size="xl"
      />

      {/* Check-Häkchen ZEICHNEN sich beim Abhaken (stroke-dashoffset,
          F500-Politur v3). Läuft beim Mount des Icons — das Icon wird
          genau dann eingewechselt, wenn der Step auf done springt. */}
      <style>{`
@media (prefers-reduced-motion: no-preference) {
  .hiw-check polyline { stroke-dasharray: 24; stroke-dashoffset: 0; animation: hiwDraw .4s ease-out; }
  @keyframes hiwDraw {
    from { stroke-dashoffset: 24; }
  }
}
      `}</style>

      {/* Das eine Produkt-Panel — Nachbau der echten Admin-Setup-Checkliste */}
      <Reveal className="mx-auto mt-14 max-w-2xl">
        <div
          ref={panelRef}
          className={`overflow-hidden ${RADII.card} border border-gray-200/80 bg-white ${SHADOW.floating}`}
        >
          {/* Panel-Header: Label + Progress-Ring */}
          <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4 sm:px-6">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-400">
                {t('checklist.label')}
              </p>
              <p
                className="mt-0.5 text-sm font-semibold text-gray-950 tabular-nums"
                aria-live="polite"
              >
                {doneCount}/{STEP_KEYS.length} {t('checklist.done')}
              </p>
            </div>
            <ProgressRing pct={pct} complete={allDone} />
          </div>

          {/* Steps */}
          <ol role="list" className="divide-y divide-gray-100">
            {STEP_KEYS.map((key, i) => {
              const isDone = i < doneCount;
              return (
                <li
                  key={key}
                  className={`flex items-start gap-4 px-5 py-4 transition-colors duration-500 sm:px-6 ${
                    isDone ? 'bg-emerald-50/40' : 'bg-white'
                  }`}
                >
                  <span
                    className={`mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold tabular-nums transition-colors duration-300 ${
                      isDone
                        ? 'bg-emerald-500 text-white motion-safe:animate-in motion-safe:zoom-in-75 motion-safe:duration-200'
                        : 'border-2 border-gray-200 text-gray-400'
                    }`}
                    aria-hidden="true"
                  >
                    {isDone ? <CheckIcon /> : i + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-semibold text-gray-950 sm:text-base">
                      {t(`steps.${key}.title`)}
                    </h3>
                    <p className="mt-0.5 text-xs leading-relaxed text-gray-500 sm:text-sm">
                      {t(`steps.${key}.desc`)}
                    </p>
                  </div>
                </li>
              );
            })}
          </ol>

          {/* Outcome-Zeile — erscheint, wenn alle 4 grün sind */}
          <div
            className={`border-t border-emerald-100 bg-emerald-50/80 px-5 py-4 transition-[opacity,transform] duration-500 sm:px-6 ${
              allDone
                ? 'opacity-100 translate-y-0'
                : 'motion-safe:opacity-0 motion-safe:translate-y-1'
            }`}
          >
            <p className="flex items-start gap-3 text-sm font-medium text-emerald-900 sm:text-base">
              <span
                className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white"
                aria-hidden="true"
              >
                <CheckIcon />
              </span>
              {t('outcome')}
            </p>
          </div>
        </div>
      </Reveal>

      {/* Inline CTA */}
      <Reveal delay={120}>
        <div className="mt-10 flex justify-center">
          <SignupCTA variant="primary" size="lg" source="how_it_works">
            {t('ctaInline')} →
          </SignupCTA>
        </div>
      </Reveal>
    </Section>
  );
}

// ─── Progress-Ring (SVG, animiert via stroke-dashoffset) ───────────────

const RING_R = 16;
const RING_C = 2 * Math.PI * RING_R;

function ProgressRing({ pct, complete }: { pct: number; complete: boolean }) {
  return (
    <div className="relative h-12 w-12" aria-hidden="true">
      <svg viewBox="0 0 40 40" className="h-full w-full -rotate-90">
        <circle
          cx="20"
          cy="20"
          r={RING_R}
          fill="none"
          strokeWidth="3.5"
          className="stroke-gray-100"
        />
        <circle
          cx="20"
          cy="20"
          r={RING_R}
          fill="none"
          strokeWidth="3.5"
          strokeLinecap="round"
          strokeDasharray={RING_C}
          strokeDashoffset={RING_C * (1 - pct / 100)}
          className={`transition-[stroke-dashoffset,stroke] duration-700 ease-out ${
            complete ? 'stroke-emerald-500' : 'stroke-blue-600'
          }`}
        />
      </svg>
      <span
        className={`absolute inset-0 flex items-center justify-center text-[10px] font-bold tabular-nums transition-colors duration-500 ${
          complete ? 'text-emerald-600' : 'text-gray-700'
        }`}
      >
        {pct}%
      </span>
    </div>
  );
}

function CheckIcon() {
  return (
    <svg
      className="hiw-check"
      viewBox="0 0 24 24"
      width="12"
      height="12"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
